import React from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Text } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { H1, H2, Body1, Body2, Caption } from '../../components/common/TextStyles';

const HelpScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  const faqs = [
    {
      question: "How do I earn XP?",
      answer: "You earn XP (Experience Points) by completing courses in the Soma section, attending events (Activities), and interacting with mentors."
    },
    {
      question: "What is the Passbook?",
      answer: "Your Passbook stores all the activities (events) you have joined. You can view them on your dashboard and check in using QR codes at physical events."
    },
    {
      question: "How do I find a mentor?",
      answer: "Navigate to the Mentor Hub from the Discovery Hub on your dashboard. You can search for mentors by category like Business, Tech, or Career."
    },
    {
      question: "What is Pesa?",
      answer: "Pesa is our financial center where you can manage your budget, save for goals, join investment groups, and explore the Marketplace."
    }
  ];

  return (
    <View style={styles.container}>
      <StatusBar style="dark" />
      <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#1E293B" />
        </TouchableOpacity>
        <H2 style={styles.headerTitle}>Help Center</H2>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <View style={styles.heroSection}>
          <LinearGradient colors={['#6366F1', '#4F46E5']} style={styles.heroGradient}>
            <Ionicons name="help-buoy" size={48} color="#FFFFFF" opacity={0.3} style={styles.heroIcon} />
            <H1 style={styles.heroTitle}>How can we help you today?</H1>
            <Body2 style={styles.heroSubtitle}>Find answers to the most common questions about Youth Connect.</Body2>
          </LinearGradient>
        </View>

        <View style={styles.section}>
          <H2 style={styles.sectionTitle}>Frequently Asked Questions</H2>
          {faqs.map((faq, index) => (
            <View key={index} style={styles.faqCard}>
              <View style={styles.faqHeader}>
                <View style={styles.faqIcon}>
                  <Ionicons name="help-circle" size={20} color="#6366F1" />
                </View>
                <Body1 style={styles.faqQuestion}>{faq.question}</Body1>
              </View>
              <Body2 style={styles.faqAnswer}>{faq.answer}</Body2>
            </View>
          ))}
        </View>

        <View style={styles.contactSection}>
          <H2 style={styles.sectionTitle}>Still need help?</H2>
          <TouchableOpacity style={styles.contactCard}>
            <View style={styles.contactIconBg}>
              <Ionicons name="mail" size={20} color="#FFFFFF" />
            </View>
            <View>
              <Body1 style={styles.contactLabel}>Email Support</Body1>
              <Caption style={styles.contactValue}>support@youthconnect.app</Caption>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#CBD5E1" style={{ marginLeft: 'auto' }} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.contactCard}>
            <View style={[styles.contactIconBg, { backgroundColor: '#10B981' }]}>
              <Ionicons name="chatbubbles" size={20} color="#FFFFFF" />
            </View>
            <View>
              <Body1 style={styles.contactLabel}>Community Chat</Body1>
              <Caption style={styles.contactValue}>Join our Telegram group</Caption>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#CBD5E1" style={{ marginLeft: 'auto' }} />
          </TouchableOpacity>
        </View>

        <View style={styles.footer}>
          <Caption style={styles.versionText}>Youth Connect v1.2.0 • Build 2026.03.16</Caption>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    backgroundColor: '#FFFFFF',
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    color: '#1E293B',
  },
  scrollContent: {
    paddingBottom: 40,
  },
  heroSection: {
    padding: 20,
  },
  heroGradient: {
    borderRadius: 20,
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
  },
  heroIcon: {
    position: 'absolute',
    right: -10,
    top: -10,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    marginBottom: 8,
  },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    lineHeight: 20,
  },
  section: {
    paddingHorizontal: 20,
    marginTop: 10,
  },
  sectionTitle: {
    fontSize: 16,
    color: '#1E293B',
    marginBottom: 15,
  },
  faqCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  faqHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 12,
  },
  faqIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  faqQuestion: {
    flex: 1,
    fontWeight: '700',
    color: '#334155',
  },
  faqAnswer: {
    color: '#64748B',
    lineHeight: 20,
    paddingLeft: 44,
  },
  contactSection: {
    paddingHorizontal: 20,
    marginTop: 20,
  },
  contactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 15,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    gap: 15,
  },
  contactIconBg: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#6366F1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactLabel: {
    fontWeight: '700',
    color: '#334155',
    marginBottom: 2,
  },
  contactValue: {
    color: '#94A3B8',
  },
  footer: {
    alignItems: 'center',
    marginTop: 30,
  },
  versionText: {
    color: '#CBD5E1',
  }
});

export default HelpScreen;
